import { useCallback } from 'react';
import type {
  ReactElement,
  ReactNode,
} from 'react';

import styled from 'styled-components';

import { Checkbox } from '../Checkbox';
import type {
  CheckboxValue,
  OnCheckboxChange,
} from '../Checkbox';

export interface CheckboxGroupOption extends Record<string, unknown> {
  help_text?: ReactNode;
  label?: ReactNode;
  value?: CheckboxValue;
}

export type CheckboxGroupProps = {
  /**
   * инлайновый вывод чекбоксов
   */
  inline?: boolean;

  /**
  * name каждого html-элемента input
  */
  name?: string;

  /**
  * массив значений выбранных опций
  */
  value: CheckboxValue[];
  /**
  * выключена ли данная группа чекбоксов
  */
  disabled?: boolean;
  /**
   * список опций данной группы чекбоксов
   */
  options: CheckboxGroupOption[];

  /**
  * ключ, по которому хранятся значения в объектах options
  */
  valueKey?: string;
  /**
  * ключ, по которому хранятся заголовки в объектах options
  */
  labelKey?: string;
  /**
  * ключ, по которому хранится текст для всплывающей подсказки в объектах options
  */
  helpTextKey?: string;

  /**
  * хэндлер изменения значения
  */
  onChange?: (nextValue: CheckboxValue[]) => void;
};

export const StyledItemWrapper = styled.div<{
  $isInline: boolean;
}>(({
  $isInline,

  theme: {
    indents,
  },
}) => {
  if ($isInline) {
    return {
      display: 'inline-block',
      marginTop: 0,
      marginRight: indents.INDENT_2,
    };
  }

  return {
    marginTop: indents.INDENT_1,

    '&:first-child': {
      marginTop: 0,
    },
  };
});

export function CheckboxGroup({
  inline = false,

  name = '',
  value,
  disabled = false,
  options,

  helpTextKey = 'help_text',
  valueKey = 'value',
  labelKey = 'label',

  onChange: onChangeProp = undefined,
}: CheckboxGroupProps): ReactElement {
  const onChange = useCallback<OnCheckboxChange>((checked, changedItemValue) => {
    if (!onChangeProp) {
      return;
    }

    if (checked) {
      onChangeProp([...value, changedItemValue]);
    } else {
      onChangeProp(value.filter((valueItem) => valueItem !== changedItemValue));
    }
  }, [
    value,
    onChangeProp,
  ]);

  return (
    <div>
      {
        options.map((option) => {
          const optionValue = option[valueKey] as string | number;
          const optionLabel = option[labelKey as 'label'];
          const optionHelpText = option[helpTextKey as 'help_text'];

          return (
            <StyledItemWrapper
              $isInline={inline}
              key={optionValue}
            >
              <Checkbox
                value={optionValue}
                checked={value.includes(optionValue)}
                disabled={disabled}
                helpText={optionHelpText}
                name={name}
                label={optionLabel}
                onChange={onChange}
              />
            </StyledItemWrapper>
          );
        })
      }
    </div>
  );
}
